import { ChevronDown, RotateCcw } from 'lucide-react'
import IndicatorConfigModal from '@/components/Indicators/IndicatorConfigModal'
import ColorPickerField from '@/components/Indicators/ColorPickerField'
import SliderField from '@/components/Indicators/SliderField'
import ToggleField from '@/components/Indicators/ToggleField'
import {
  SAFE_DEFAULTS,
  QUANTUM_COLORS,
  PERIOD_RANGE,
  MULTIPLIER_RANGE,
  SQUEEZE_RANGE,
  LINE_WIDTHS,
  resolveColor,
} from './constants.js'

function Section({ title, children, sectionKey, config, onChange }) {
  const open = config?.panelSections?.[sectionKey] ?? false
  const toggle = () => onChange({ ...config, panelSections: { ...(config.panelSections || {}), [sectionKey]: !open } })
  return (
    <div className="border border-[var(--hair,rgba(176,141,87,0.35))] rounded-lg overflow-hidden">
      <button
        onClick={toggle}
        className="w-full flex items-center justify-between px-2.5 py-2 bg-[var(--paper,#FDFDFA)] hover:bg-black/[0.02] transition-colors"
      >
        <span className="text-xs font-semibold uppercase tracking-wider text-[var(--gold,#B08D57)]">{title}</span>
        <ChevronDown size={14} className={`text-[var(--ink-soft,#4A5A52)] transition-transform ${open ? '' : '-rotate-90'}`} />
      </button>
      {open && (
        <div className="px-2.5 py-2 space-y-2 bg-white/50 dark:bg-gray-900/20">
          {children}
        </div>
      )}
    </div>
  )
}

function ChipSelect({ label, options, value, onChange }) {
  return (
    <div className="space-y-1">
      <span className="text-xs text-[var(--ink-soft,#4A5A52)]">{label}</span>
      <div className="grid grid-cols-4 gap-1">
        {options.map((opt) => (
          <button
            key={String(opt.value)}
            type="button"
            aria-pressed={value === opt.value}
            onClick={() => onChange(opt.value)}
            className={`px-1 py-1 text-[10px] font-medium rounded border transition-colors ${
              value === opt.value
                ? 'bg-[var(--paper,#FDFDFA)] border-[var(--gold,#B08D57)] text-[var(--green-deep,#0B3D2E)]'
                : 'bg-white dark:bg-gray-800 border-slate-200 dark:border-gray-700 text-slate-600 dark:text-gray-400 hover:bg-slate-50 dark:hover:bg-gray-700'
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  )
}

const PRICE_TYPE_OPTIONS = [
  { value: 'close', label: 'Close' },
  { value: 'hl2', label: 'HL2' },
  { value: 'hlc3', label: 'HLC3' },
  { value: 'ohlc4', label: 'OHLC4' },
]

const STYLE_KEYS = [
  'upperColor', 'lowerColor', 'middleColor', 'fillColor', 'squeezeColor',
  'lineWidth', 'middleLineStyle',
]

export default function BollingerBandsQuantumPanel({ config, onChange, onClose }) {
  const cfg = config || {}

  const update = (key, value) => onChange({ ...cfg, [key]: value })

  const resetStyle = () => {
    const patch = {}
    for (const k of STYLE_KEYS) patch[k] = SAFE_DEFAULTS[k]
    onChange({ ...cfg, ...patch })
  }

  return (
    <IndicatorConfigModal title="Configuración Bollinger Bands Quantum" isOpen onClose={onClose}>
      <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">

        <Section title="Activación" sectionKey="activation" config={cfg} onChange={onChange}>
          <ToggleField
            label="Indicador activo"
            value={cfg.enabled ?? true}
            onChange={(v) => update('enabled', v)}
          />
          <p className="text-[10px] text-[var(--ink-soft,#4A5A52)] leading-relaxed">
            Apagado suave: oculta las bandas sin quitar el indicador del menú. El alta/baja definitiva se hace desde el menú de indicadores.
          </p>
        </Section>

        <Section title="Cálculo" sectionKey="calc" config={cfg} onChange={onChange}>
          <SliderField
            label="Período"
            value={cfg.period ?? SAFE_DEFAULTS.period}
            min={PERIOD_RANGE.min}
            max={PERIOD_RANGE.max}
            step={1}
            unit=" barras"
            onChange={(v) => update('period', v)}
          />
          <SliderField
            label="Multiplicador"
            value={cfg.multiplier ?? SAFE_DEFAULTS.multiplier}
            min={MULTIPLIER_RANGE.min}
            max={MULTIPLIER_RANGE.max}
            step={0.1}
            unit=" σ"
            onChange={(v) => update('multiplier', v)}
          />
          <ChipSelect
            label="Tipo de precio"
            options={PRICE_TYPE_OPTIONS}
            value={cfg.priceType ?? SAFE_DEFAULTS.priceType}
            onChange={(v) => update('priceType', v)}
          />
        </Section>

        <Section title="Visualización" sectionKey="visual" config={cfg} onChange={onChange}>
          <ToggleField label="Banda superior" value={cfg.showUpper ?? true} onChange={(v) => update('showUpper', v)} />
          <ToggleField label="Banda media" value={cfg.showMiddle ?? true} onChange={(v) => update('showMiddle', v)} />
          <ToggleField label="Banda inferior" value={cfg.showLower ?? true} onChange={(v) => update('showLower', v)} />
          <ToggleField label="Relleno entre bandas" value={cfg.showFill ?? true} onChange={(v) => update('showFill', v)} />
          <ToggleField label="Etiquetas de valores" value={cfg.showLabels ?? true} onChange={(v) => update('showLabels', v)} />
          <ToggleField label="Marca de squeeze" value={cfg.showSqueeze ?? true} onChange={(v) => update('showSqueeze', v)} />
          <SliderField
            label="Umbral de squeeze (bandwidth)"
            value={cfg.squeezeThreshold ?? SAFE_DEFAULTS.squeezeThreshold}
            min={SQUEEZE_RANGE.min}
            max={SQUEEZE_RANGE.max}
            step={0.5}
            unit="%"
            onChange={(v) => update('squeezeThreshold', v)}
          />
        </Section>

        <Section title="Colores" sectionKey="colors" config={cfg} onChange={onChange}>
          <ColorPickerField label="Banda superior" value={resolveColor(cfg.upperColor, QUANTUM_COLORS.bear)} onChange={(v) => update('upperColor', v)} />
          <ColorPickerField label="Banda inferior" value={resolveColor(cfg.lowerColor, QUANTUM_COLORS.bull)} onChange={(v) => update('lowerColor', v)} />
          <ColorPickerField label="Banda media" value={resolveColor(cfg.middleColor, QUANTUM_COLORS.neutral)} onChange={(v) => update('middleColor', v)} />
          <ColorPickerField label="Relleno" value={resolveColor(cfg.fillColor, QUANTUM_COLORS.bull)} onChange={(v) => update('fillColor', v)} />
          <ColorPickerField label="Squeeze" value={resolveColor(cfg.squeezeColor, QUANTUM_COLORS.squeeze)} onChange={(v) => update('squeezeColor', v)} />
        </Section>

        <Section title="Estilo" sectionKey="style" config={cfg} onChange={onChange}>
          <ChipSelect
            label="Grosor de línea"
            options={LINE_WIDTHS.map(w => ({ value: w, label: String(w) }))}
            value={cfg.lineWidth ?? SAFE_DEFAULTS.lineWidth}
            onChange={(v) => update('lineWidth', v)}
          />
          <ChipSelect
            label="Estilo banda media"
            options={[
              { value: 'solid', label: 'Sólido' },
              { value: 'dotted', label: 'Puntos' },
            ]}
            value={cfg.middleLineStyle ?? SAFE_DEFAULTS.middleLineStyle}
            onChange={(v) => update('middleLineStyle', v)}
          />
          <button
            onClick={resetStyle}
            className="w-full flex items-center justify-center gap-1.5 py-1.5 text-[11px] font-medium rounded border border-[var(--hair,rgba(176,141,87,0.35))] bg-[var(--paper,#FDFDFA)] text-[var(--green-deep,#0B3D2E)] hover:bg-[var(--gold,#B08D57)] hover:text-white transition-colors"
          >
            <RotateCcw size={12} />
            Restablecer estilo Quantum
          </button>
        </Section>

      </div>
    </IndicatorConfigModal>
  )
}
